import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "react-router-dom";
import {
  upsertSectionDraft,
  publishSection,
} from "../../../../../Services/adminCorporates";
import amplitudeService from "../../../../../Services/amplitude";
import { Button } from "../../../../../ui/button";

/**
 * Wraps each section editor with a consistent form/save/publish frame.
 *
 * The render-prop pattern (`children`) gives the editor full access to RHF
 * state (register / control / errors / watch / setValue) without each editor
 * needing to repeat boilerplate.
 *
 * Props:
 *  - sectionKey: string (e.g. "hero")
 *  - schema: zod schema
 *  - defaults: default values when section has no draft yet
 *  - section: current section record from the API (may be null for "not yet authored")
 *  - onSaved(updatedSection): called after a successful save or publish so the
 *    parent can update its section list (status badge etc.)
 *  - children(form): function that receives the RHF instance and returns the fields JSX
 */
const EditorShell = ({
  sectionKey,
  schema,
  defaults,
  section,
  onSaved,
  onLiveChange,
  children,
  pageKey = "activation",
  transformBeforeSave,
}) => {
  const { id } = useParams();
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [status, setStatus] = useState(null);
  const [serverError, setServerError] = useState(null);
  const lastInitRef = useRef(null);

  const initialValues = section?.content || defaults;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  // Reset form when the parent switches to a different section, or when the
  // saved content materially changes (e.g. after publish, the API echoes back
  // a slightly normalized object).
  useEffect(() => {
    const stamp = JSON.stringify({ sectionKey, content: initialValues });
    if (lastInitRef.current !== stamp) {
      form.reset(initialValues);
      lastInitRef.current = stamp;
      setStatus(null);
      setServerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, section?.published_version, section?.updated_at]);

  // Stream form values to the parent so it can render a live preview as the
  // admin types. We deliberately don't include `onLiveChange` in deps — it's
  // expected to be a stable reference; including it would resubscribe on
  // every render.
  useEffect(() => {
    if (!onLiveChange) return undefined;
    onLiveChange(form.getValues());
    const sub = form.watch((values) => onLiveChange(values));
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  // Map server validation errors back onto the form fields.
  const applyServerErrors = (err) => {
    const detail = err?.response?.data?.payload?.errors;
    if (Array.isArray(detail)) {
      detail.forEach((entry) => {
        if (!entry?.path) return;
        form.setError(entry.path, { message: entry.message || "Invalid value" });
      });
    }
    setServerError(
      err?.response?.data?.message || err?.message || "Save failed"
    );
  };

  const buildSavePayload = (values) =>
    transformBeforeSave ? transformBeforeSave(values) : values;

  const handleSave = form.handleSubmit(async (values) => {
    setSaving(true);
    setStatus(null);
    setServerError(null);
    try {
      const updated = await upsertSectionDraft(
        id,
        pageKey,
        sectionKey,
        buildSavePayload(values)
      );
      onSaved?.(updated);
      setStatus("Draft saved.");
    } catch (err) {
      applyServerErrors(err);
    } finally {
      setSaving(false);
    }
  });

  const handlePublish = async () => {
    setPublishing(true);
    setStatus(null);
    setServerError(null);
    try {
      // Persist the latest form values as a draft first so what we publish
      // matches what the admin sees.
      const values = form.getValues();
      await upsertSectionDraft(
        id,
        pageKey,
        sectionKey,
        buildSavePayload(values)
      );
      const published = await publishSection(id, pageKey, sectionKey);
      onSaved?.(published);
      amplitudeService.track?.("admin_section_published", {
        corporate_id: id,
        section_key: sectionKey,
      });
      setStatus("Published.");
    } catch (err) {
      applyServerErrors(err);
    } finally {
      setPublishing(false);
    }
  };

  // Disable Publish when we know the section is empty / required fields are
  // missing — saves an obvious round-trip even though the API also rejects.
  const formValues = form.watch();
  const canPublish = (() => {
    const parsed = schema.safeParse(formValues);
    return parsed.success;
  })();

  return (
    <form onSubmit={handleSave} noValidate className="space-y-5">
      {children(form)}

      {serverError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {serverError}
        </div>
      )}

      <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-zinc-200 flex items-center justify-end gap-3">
        {status && (
          <span className="text-sm text-emerald-600 mr-auto">{status}</span>
        )}
        <Button
          type="submit"
          variant="outline"
          disabled={saving || publishing}
        >
          {saving ? "Saving…" : "Save draft"}
        </Button>
        <Button
          type="button"
          onClick={handlePublish}
          disabled={publishing || saving || !canPublish}
          title={!canPublish ? "Fill in required fields before publishing" : undefined}
        >
          {publishing ? "Publishing…" : "Publish"}
        </Button>
      </div>
    </form>
  );
};

export default EditorShell;
